/**
 * Calendar date helpers — timezone-safe.
 *
 * Why: cargo_ready_date and similar fields are stored as PG `DATE` (calendar
 * date, no time, no timezone). When the backend serialises a Date object to
 * JSON it becomes an ISO timestamp at midnight UTC — and `new Date(isoString)`
 * + `.toLocaleDateString()` shifts to the user's local timezone. For anyone
 * west of UTC, May 15 displays as May 14.
 *
 * These helpers parse the date parts directly so the calendar value is
 * preserved across timezones.
 */

/**
 * Pull a YYYY-MM-DD string out of any common date input shape:
 *   "2026-05-15"                          → "2026-05-15"
 *   "2026-05-15T00:00:00.000Z"            → "2026-05-15"
 *   Date object (from backend round-trip) → "YYYY-MM-DD" of its UTC date
 *   null / undefined / ""                  → ""
 */
export function normaliseYMD(input) {
  if (input == null || input === '') return ''
  if (input instanceof Date) {
    if (Number.isNaN(input.getTime())) return ''
    const y = input.getUTCFullYear()
    const m = String(input.getUTCMonth() + 1).padStart(2, '0')
    const d = String(input.getUTCDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }
  const s = String(input)
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  return match ? `${match[1]}-${match[2]}-${match[3]}` : ''
}

/**
 * Format a calendar date for display without timezone round-tripping.
 *
 *   formatYMD('2026-05-15')                                    → 'May 15, 2026'
 *   formatYMD('2026-05-15T00:00:00.000Z')                      → 'May 15, 2026'
 *   formatYMD('2026-05-15', { month: 'short', day: 'numeric' }) → 'May 15'
 *   formatYMD('')                                              → ''
 *   formatYMD('not-a-date')                                    → ''
 */
export function formatYMD(input, opts = { month: 'short', day: 'numeric', year: 'numeric' }) {
  const ymd = normaliseYMD(input)
  if (!ymd) return ''
  // Anchor the date at noon UTC and ask Intl to format in UTC. Constructing a
  // local-midnight Date kept the calendar day stable for most browsers, but
  // failed for some Date constructors east of UTC near month boundaries —
  // anchoring at noon and forcing the formatter into UTC removes the drift in
  // every timezone.
  const anchored = new Date(`${ymd}T12:00:00Z`)
  return anchored.toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' })
}
